import { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";
import { Link } from "react-router-dom";
import { Users, Hammer, Inbox, AlertTriangle, ArrowUpRight, ShieldAlert, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";

const COLORS = ["#C8A951", "#0A0A0A", "#7A7A7A", "#B97A3F", "#3A4F5A", "#A88944"];

export default function DashboardOverview() {
  const [data, setData] = useState(null);
  const [docsSummary, setDocsSummary] = useState({ counts: {}, upcoming: [] });

  useEffect(() => {
    api.get("/admin/analytics/overview").then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/admin/documents/summary?threshold_days=30").then((r) => setDocsSummary(r.data)).catch(() => {});
  }, []);

  const kpis = data?.kpis || {};
  const monthly = (data?.monthly_requests || []).map((m) => ({ month: m.month?.slice(5) || m.month, count: m.count }));
  const empDist = data?.employee_distribution || [];
  const svcDist = data?.service_distribution || [];

  return (
    <div className="space-y-10" data-testid="dashboard-overview">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="overline">/ Operations Console</span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">Today, across the Kingdom.</h1>
          <p className="text-muted-foreground mt-2">
            A live snapshot of MNH operations, workforce and inbound demand.
          </p>
        </div>
      </header>

      {/* Compliance alert banner */}
      <ComplianceBanner summary={docsSummary} />


      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5" data-testid="kpi-grid">
        <KpiCard testid="kpi-employees" icon={Users} label="Active Workforce" value={kpis.employees} suffix="+" trend="+3.2%" />
        <KpiCard testid="kpi-projects" icon={Hammer} label="Active Projects" value={kpis.active_projects} trend="+1" />
        <KpiCard testid="kpi-requests" icon={Inbox} label="Pending Requests" value={kpis.pending_requests} trend="new" trendType="muted" />
        <KpiCard testid="kpi-stock" icon={AlertTriangle} label="Low Stock Items" value={kpis.low_stock_items} trend="alert" trendType="warning" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 p-6 rounded-sm" data-testid="chart-monthly-requests">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Demand Trend</div>
              <h3 className="text-lg font-bold mt-1">Monthly inbound requests</h3>
            </div>
            <span className="text-xs text-muted-foreground">Last 6 months</span>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly.length ? monthly : [{ month: "—", count: 0 }]}>
                <CartesianGrid strokeDasharray="2 4" stroke="#e5e5e5" vertical={false} />
                <XAxis dataKey="month" stroke="#8a8a8a" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#8a8a8a" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid #d4d4d4", fontSize: 12 }} />
                <Bar dataKey="count" fill="#C8A951" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 rounded-sm" data-testid="chart-employee-dist">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Workforce</div>
          <h3 className="text-lg font-bold mt-1">By department</h3>
          <div className="h-72 mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={empDist.length ? empDist : [{ department: "—", count: 1 }]}
                  dataKey="count"
                  nameKey="department"
                  innerRadius={48}
                  outerRadius={88}
                  paddingAngle={2}
                >
                  {(empDist.length ? empDist : [{}]).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid #d4d4d4", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="square" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 p-6 rounded-sm bg-secondary text-secondary-foreground border-secondary grain" data-testid="chart-service-mix">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Service Mix</div>
          <h3 className="text-lg font-bold mt-1">Active projects by service line</h3>
          <div className="h-72 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={svcDist.length ? svcDist : [{ service: "—", count: 0 }]}>
                <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="service" stroke="#bdbdbd" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#bdbdbd" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontSize: 12 }} />
                <Line type="monotone" dataKey="count" stroke="#C8A951" strokeWidth={3} dot={{ fill: "#C8A951", r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 rounded-sm" data-testid="dashboard-vision-card">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Vision 2030</div>
          <h3 className="text-2xl font-bold mt-2">Operational Excellence</h3>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
            MNH operations align with the Kingdom's National Industrial Development &amp; Logistics Program —
            powering airports, megaprojects, and public-sector facilities at scale.
          </p>
          <div className="mt-6 flex items-center gap-2 text-sm text-primary font-semibold">
            View HSE & SLA report <ArrowUpRight className="h-4 w-4" />
          </div>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, suffix = "", trend, trendType = "up", testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid={testid}
    >
      <Card className="p-6 rounded-sm h-full hover:border-primary transition-colors">
        <div className="flex items-start justify-between">
          <div className="h-10 w-10 bg-secondary text-primary flex items-center justify-center">
            <Icon className="h-4 w-4" />
          </div>
          <span
            className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${
              trendType === "warning"
                ? "bg-destructive/10 text-destructive"
                : trendType === "muted"
                ? "bg-muted text-muted-foreground"
                : "bg-primary/10 text-primary"
            }`}
          >
            {trend}
          </span>
        </div>
        <div className="mt-6 text-4xl font-black tabular-nums">
          {(value ?? 0).toLocaleString()}
          {suffix}
        </div>
        <div className="mt-1 text-sm text-muted-foreground">{label}</div>
      </Card>
    </motion.div>
  );
}

function ComplianceBanner({ summary }) {
  const counts = summary?.counts || {};
  const upcoming = summary?.upcoming || [];
  const expired = counts.expired || 0;
  const expiring = counts.expiring || 0;
  const alertCount = expired + expiring;

  if (alertCount === 0) {
    return (
      <div className="flex items-center justify-between gap-4 p-4 rounded-sm border border-emerald-200 bg-emerald-50" data-testid="compliance-banner-clear">
        <div className="flex items-center gap-3">
          <ShieldAlert className="h-5 w-5 text-emerald-700" />
          <div>
            <div className="text-sm font-bold text-emerald-800">All documents valid</div>
            <div className="text-xs text-emerald-700/80">No Iqama or passport expiries in the next 30 days.</div>
          </div>
        </div>
        <Link to="/admin/compliance" className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-800 hover:underline inline-flex items-center gap-1.5">
          View tracker <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    );
  }

  const tone = expired > 0
    ? "border-red-300 bg-red-50"
    : "border-amber-300 bg-amber-50";

  return (
    <Link
      to="/admin/compliance"
      className={`block rounded-sm border ${tone} p-5 hover:shadow-md transition-shadow`}
      data-testid="compliance-banner-alert"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`h-10 w-10 grid place-items-center rounded-sm ${expired > 0 ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-bold">
              {expired > 0 && <span className="text-red-700">{expired} expired</span>}
              {expired > 0 && expiring > 0 && <span className="text-foreground/60"> · </span>}
              {expiring > 0 && <span className="text-amber-800">{expiring} expiring within 30 days</span>}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Open the Compliance tracker to renew documents before they lapse.
            </div>
            {upcoming.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs text-foreground/80">
                {upcoming.slice(0, 3).map((u) => (
                  <li key={u.id} className="flex items-center gap-2">
                    <span className="font-semibold">{u.employee_name}</span>
                    {u.iqama_status === "expired" && <span className="text-red-700">· Iqama expired {Math.abs(u.iqama_days_left)}d ago</span>}
                    {u.iqama_status === "expiring" && <span className="text-amber-800">· Iqama expires in {u.iqama_days_left}d</span>}
                    {u.passport_status === "expired" && <span className="text-red-700">· Passport expired {Math.abs(u.passport_days_left)}d ago</span>}
                    {u.passport_status === "expiring" && <span className="text-amber-800">· Passport expires in {u.passport_days_left}d</span>}
                  </li>
                ))}
                {upcoming.length > 3 && (
                  <li className="text-muted-foreground">+ {upcoming.length - 3} more…</li>
                )}
              </ul>
            )}
          </div>
        </div>
        <ArrowRight className="h-4 w-4 mt-2 shrink-0" />
      </div>
    </Link>
  );
}

